package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminLegacyCrudApi
import twist.mena.sports.admin.components.*
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.OffsetPage
import twist.mena.sports.shared.dto.admin.legacy.AdminLegacyEntityResponse

@Composable
fun LegacyCrudTeamsScreen(legacyCrudApi: AdminLegacyCrudApi) {
    LegacyCrudEntityScreen(legacyCrudApi, "teams", "team")
}

@Composable
fun LegacyCrudLeaguesScreen(legacyCrudApi: AdminLegacyCrudApi) {
    LegacyCrudEntityScreen(legacyCrudApi, "leagues", "league")
}

@Composable
fun LegacyCrudPlayersScreen(legacyCrudApi: AdminLegacyCrudApi) {
    LegacyCrudEntityScreen(legacyCrudApi, "players", "player")
}

@Composable
private fun LegacyCrudEntityScreen(
    api: AdminLegacyCrudApi,
    entityType: String,
    entityLabel: String
) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdminLegacyEntityResponse>>>(UiState.Loading) }
    var search by remember { mutableStateOf("") }
    var offset by remember { mutableStateOf(0) }
    var missingTranslations by remember { mutableStateOf(false) }
    var showCreateDialog by remember { mutableStateOf(false) }
    var editingEntity by remember { mutableStateOf<AdminLegacyEntityResponse?>(null) }
    val scope = rememberCoroutineScope()
    val isEditor = AuthState.getRole()?.let {
        it in listOf("scora_editor", "scora_admin", "scora_super_admin")
    } ?: false

    fun loadEntities() {
        scope.launch {
            state = UiState.Loading
            try {
                val page = api.listEntities(entityType, search.ifBlank { null }, offset, 50, missingTranslations)
                state = UiState.Success(page)
            } catch (e: Exception) {
                state = UiState.Error("Failed to load $entityType")
            }
        }
    }

    LaunchedEffect(search, offset, missingTranslations) { loadEntities() }

    // Top controls row
    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.md); marginBottom(Spacing.lg); flexWrap(FlexWrap.Wrap) } }) {
        SearchBar(placeholderText = "Search $entityType...") { query ->
            search = query
            offset = 0
        }

        // Missing translations filter
        Div({
            onClick { missingTranslations = !missingTranslations; offset = 0 }
            style {
                padding(Spacing.sm, Spacing.md)
                borderRadius(BorderRadius.md)
                cursor("pointer")
                backgroundColor(Color(if (missingTranslations) colors.warning else colors.cardBackground))
                color(Color(if (missingTranslations) "#000000" else colors.text))
                fontSize(Typography.sm)
                property("border", "1px solid ${colors.border}")
            }
        }) { Text(if (missingTranslations) "Showing missing translations" else "Filter: missing translations") }

        // Create button
        if (isEditor) {
            Button({
                onClick { showCreateDialog = true }
                style {
                    padding(Spacing.sm, Spacing.md)
                    primaryButtonStyle(colors)
                    fontSize(Typography.sm)
                    borderRadius(BorderRadius.md)
                }
            }) { Text("Add $entityLabel") }
        }
    }

    when (val s = state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            val page = s.data
            val columns = listOf(
                Column<AdminLegacyEntityResponse>("Order") { entity ->
                    Text(entity.displayOrder.toString())
                },
                Column("Image") { entity ->
                    if (entity.imageUrl != null) {
                        Img(src = entity.imageUrl!!, alt = entity.nameEn ?: "") {
                            style { width(32.px); height(32.px); property("object-fit", "contain"); borderRadius(BorderRadius.sm) }
                        }
                    } else {
                        Span({ style { color(Color(colors.textMuted)); fontSize(Typography.xs) } }) { Text("--") }
                    }
                },
                Column("English Name") { entity ->
                    Text(entity.nameEn ?: "")
                },
                Column("Arabic Name") { entity ->
                    Span { Text(entity.nameAr ?: "") }
                    if (entity.hasMissingTranslation) {
                        Span({ style { marginLeft(Spacing.xs); color(Color(colors.warning)); fontSize(Typography.xs) } }) { Text("!") }
                    }
                },
                Column("Status") { entity ->
                    if (entity.isHidden) {
                        Span({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text("Hidden") }
                    } else {
                        Span({ style { color(Color(colors.success)); fontSize(Typography.sm) } }) { Text("Visible") }
                    }
                },
                Column("Actions") { entity ->
                    if (!isEditor) return@Column
                    Div({ style { display(DisplayStyle.Flex); gap(Spacing.xs); flexWrap(FlexWrap.Wrap); alignItems(AlignItems.Center) } }) {
                        Button({
                            onClick {
                                scope.launch {
                                    try { api.reorderEntity(entityType, entity.id, "up"); loadEntities() }
                                    catch (_: Exception) { }
                                }
                            }
                            style { padding(Spacing.xs, Spacing.sm); secondaryButtonStyle(colors); fontSize(Typography.xs) }
                        }) { Text("\u25B2") }
                        Button({
                            onClick {
                                scope.launch {
                                    try { api.reorderEntity(entityType, entity.id, "down"); loadEntities() }
                                    catch (_: Exception) { }
                                }
                            }
                            style { padding(Spacing.xs, Spacing.sm); secondaryButtonStyle(colors); fontSize(Typography.xs) }
                        }) { Text("\u25BC") }
                        Button({
                            onClick { editingEntity = entity }
                            style { padding(Spacing.xs, Spacing.sm); primaryButtonStyle(colors); fontSize(Typography.xs) }
                        }) { Text("Edit") }
                        Button({
                            onClick {
                                scope.launch {
                                    try {
                                        if (entity.isHidden) api.unhideEntity(entityType, entity.id)
                                        else api.hideEntity(entityType, entity.id)
                                        loadEntities()
                                    } catch (e: Exception) { }
                                }
                            }
                            style {
                                padding(Spacing.xs, Spacing.sm)
                                if (entity.isHidden) primaryButtonStyle(colors) else secondaryButtonStyle(colors)
                                fontSize(Typography.xs)
                            }
                        }) { Text(if (entity.isHidden) "Show" else "Hide") }
                    }
                }
            )

            DataTable(
                columns = columns,
                rows = page.data,
                total = page.total,
                offset = page.offset,
                limit = page.limit,
                onPageChange = { newOffset -> offset = newOffset }
            )
        }
    }

    // Create dialog
    if (showCreateDialog) {
        LegacyCrudEntityDialog(
            entity = null,
            entityType = entityType,
            entityLabel = entityLabel,
            api = api,
            onSaved = { loadEntities() },
            onClose = { showCreateDialog = false }
        )
    }

    // Edit dialog
    editingEntity?.let { entity ->
        LegacyCrudEntityDialog(
            entity = entity,
            entityType = entityType,
            entityLabel = entityLabel,
            api = api,
            onSaved = { loadEntities() },
            onClose = { editingEntity = null }
        )
    }
}
